import * as React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageShell from "./shared/PageShell";
import { pageStyles } from "./shared/theme";

function cleanPhoneNumber(phone) {
  return phone.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
}

function validatePhoneNumber(phone) {
  if (!phone.trim()) return "Phone number is required.";
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 10) return "Phone number must have at least 10 digits.";
  if (digits.length > 15) return "Phone number is too long.";
  if (!/^\+?[\d().\-\s]+$/.test(phone)) return "Enter a valid phone number.";
  return "";
}

export default function JoinPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const referralCode = searchParams.get("deep_link_value");
  const inviterName = searchParams.get("deep_link_sub1");

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validatePhoneNumber(phoneNumber);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const body = { phoneNumber: cleanPhoneNumber(phoneNumber) };
      if (referralCode) body.referralCode = referralCode;

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/send-download-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      if (!response.ok) throw new Error("Unable to send invite");
      navigate("/join/success");
    } catch (_requestError) {
      setError("We couldn't send the link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      footer={
        <Typography variant="body2" sx={{ color: "#52667A", textAlign: "center" }}>
          Your personal wealth manager, in your pocket.
        </Typography>
      }
    >
      <Typography component="h1" variant="h1" sx={pageStyles.title}>
        {inviterName
          ? `${inviterName} invited you to Finn`
          : "You're invited to Finn"}
      </Typography>
      <Typography variant="body1" sx={pageStyles.subtitle}>
        Enter your phone number and we'll text you a personal download link.
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert> : null}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          fullWidth
          id="phoneNumber"
          label="Phone number"
          name="phoneNumber"
          placeholder="(555) 555-5555"
          autoComplete="tel"
          value={phoneNumber}
          onChange={(event) => {
            setPhoneNumber(event.target.value);
            setError("");
          }}
          sx={pageStyles.field}
        />
        <LoadingButton
          type="submit"
          loading={loading}
          fullWidth
          variant="contained"
          sx={pageStyles.primaryButton}
        >
          Text me the app
        </LoadingButton>
      </Box>
    </PageShell>
  );
}
